// Client-side product analytics for the web app.
//
// Anonymous visitor ID: a crypto-random UUID stored in localStorage on first
// visit. It rides on every event so pre-account activity can be stitched to an
// account once the person signs in or signs up (the "identify" event).
//
// Entry source: captured once on the very first page load from the referrer
// and URL params. Stored in localStorage and attached to every event.
//
// Events are fire-and-forget POSTs to /api/track (forwarded to the core API
// worker, which writes them to Analytics Engine). A failed POST is silently
// dropped — analytics must never break the page.

import type { AnalyticsEventName } from "@shared/analytics-events.js";
import { accessToken } from "@web-shared/lib/account";

const ANON_ID_KEY = "seedbed.anonId";
const ENTRY_SOURCE_KEY = "seedbed.entrySource";

// ---------------------------------------------------------------------------
// Anonymous visitor ID
// ---------------------------------------------------------------------------

let cachedAnonId: string | undefined;

export function getAnonId(): string {
  if (cachedAnonId) return cachedAnonId;
  try {
    let id = localStorage.getItem(ANON_ID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(ANON_ID_KEY, id);
    }
    cachedAnonId = id;
    return id;
  } catch {
    // Private browsing or storage full — return a transient id.
    cachedAnonId = crypto.randomUUID();
    return cachedAnonId;
  }
}

// ---------------------------------------------------------------------------
// Entry source — captured once, on the very first visit
// ---------------------------------------------------------------------------

let cachedEntrySource: string | undefined;

function resolveEntrySource(): string {
  // Already captured on a previous visit.
  try {
    const stored = localStorage.getItem(ENTRY_SOURCE_KEY);
    if (stored) return stored;
  } catch { /* ignore */ }

  const url = new URL(window.location.href);

  // UTM params take precedence.
  const utmSource = url.searchParams.get("utm_source");
  const utmMedium = url.searchParams.get("utm_medium");
  const utmCampaign = url.searchParams.get("utm_campaign");
  if (utmSource) {
    const parts = [`utm_source=${utmSource}`];
    if (utmMedium) parts.push(`utm_medium=${utmMedium}`);
    if (utmCampaign) parts.push(`utm_campaign=${utmCampaign}`);
    return persist(parts.join("&"));
  }

  // ref= param (our own links carry it).
  const ref = url.searchParams.get("ref");
  if (ref === "extension") return persist("extension");
  if (ref === "audiotool") return persist("audiotool");
  if (ref === "web") return persist("seed-bed.com");

  // Shared seed link: arrived on /s/:id directly.
  if (/^\/s\/[^/]+$/.test(url.pathname)) return persist("shared_link");

  // Referrer-based.
  try {
    const ref = document.referrer;
    if (!ref) return persist("direct");
    const refHost = new URL(ref).hostname;
    if (refHost.endsWith("seed-bed.com")) return persist("seed-bed.com");
    if (refHost.endsWith("audiotool.com")) return persist("audiotool");
    return persist(`referrer:${refHost}`);
  } catch {
    return persist("direct");
  }
}

function persist(source: string): string {
  try { localStorage.setItem(ENTRY_SOURCE_KEY, source); } catch { /* ignore */ }
  return source;
}

export function getEntrySource(): string {
  if (cachedEntrySource) return cachedEntrySource;
  cachedEntrySource = resolveEntrySource();
  return cachedEntrySource;
}

// ---------------------------------------------------------------------------
// Event sending
// ---------------------------------------------------------------------------

interface TrackFields {
  seedId?: string;
  detail?: string;
  value?: number;
}

export function track(event: AnalyticsEventName, fields: TrackFields = {}): void {
  try {
    const token = accessToken();
    const body = JSON.stringify({
      event,
      ...fields,
      anonId: getAnonId(),
      entrySource: getEntrySource(),
      surface: "web",
    });
    fetch("/api/track", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // analytics must never throw
  }
}

// ---------------------------------------------------------------------------
// Identify — link anonId to a signed-in account
// ---------------------------------------------------------------------------

export function identify(): void {
  track("identify", { detail: getAnonId() });
}
